import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, Home as HomeIcon, Plus, Trash2, X } from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn, formatTime } from '@/lib/utils';
import type {
  CorrectionAction,
  CorrectionDeclaredLocation,
  CorrectionPunchType,
  CorrectionSummary,
} from './types';

interface DayPunch {
  id: string;
  type: CorrectionPunchType;
  eventAt: string;
  declaredLocation: CorrectionDeclaredLocation;
}

interface DraftItem {
  key: string;
  action: CorrectionAction;
  targetPunchId?: string;
  proposedTime?: string;
  proposedType?: CorrectionPunchType;
  declaredLocation: CorrectionDeclaredLocation;
}

interface Props {
  open: boolean;
  onClose: () => void;
  targetDate: string;
  punches: DayPunch[];
  /** Si viene, la corrección es sobre este empleado (modo administración RRHH). */
  targetUserId?: string;
  /** Nombre del empleado objetivo, para el título en modo administración. */
  targetName?: string;
  /** Callback opcional tras enviar con éxito (p. ej. recargar la jornada). */
  onSubmitted?: () => void;
}

export function CorrectionRequestModal({
  open,
  onClose,
  targetDate,
  punches,
  targetUserId,
  targetName,
  onSubmitted,
}: Props) {
  const queryClient = useQueryClient();
  const [items, setItems] = useState<DraftItem[]>([]);
  const [reason, setReason] = useState('');

  const createMutation = useMutation({
    mutationFn: () => {
      const payload = items.map((it) => buildPayloadItem(it, targetDate));
      return api<CorrectionSummary>('/corrections', {
        method: 'POST',
        body: JSON.stringify({
          targetDate,
          reason: reason.trim(),
          items: payload,
          ...(targetUserId ? { targetUserId } : {}),
        }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['corrections'] });
      onSubmitted?.();
      onClose();
      reset();
    },
  });

  if (!open) return null;

  function reset() {
    setItems([]);
    setReason('');
    createMutation.reset();
  }

  function addItem(action: CorrectionAction, targetPunchId?: string) {
    const orig = targetPunchId ? punches.find((p) => p.id === targetPunchId) : undefined;
    setItems((prev) => [
      ...prev,
      {
        key: `${Date.now()}-${prev.length}`,
        action,
        targetPunchId,
        proposedType: action === 'ADD' ? 'IN' : undefined,
        declaredLocation: action === 'MODIFY' ? (orig?.declaredLocation ?? 'OFFICE') : 'OFFICE',
      },
    ]);
  }

  function updateItem(key: string, patch: Partial<DraftItem>) {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  }

  function removeItem(key: string) {
    setItems((prev) => prev.filter((it) => it.key !== key));
  }

  const usedPunchIds = new Set(items.map((i) => i.targetPunchId).filter(Boolean) as string[]);
  const availablePunches = punches.filter((p) => !usedPunchIds.has(p.id));
  const canSubmit = items.length > 0 && reason.trim().length >= 5 && !createMutation.isPending;

  const error = createMutation.error
    ? createMutation.error instanceof ApiError
      ? createMutation.error.message
      : 'Error al enviar la solicitud'
    : null;

  function handleClose() {
    if (createMutation.isPending) return;
    reset();
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        aria-hidden
        onClick={handleClose}
        className="absolute inset-0"
        style={{ background: 'rgba(10,14,31,0.4)', backdropFilter: 'blur(2px)' }}
      />
      <div className="relative w-full max-w-[600px] max-h-[90vh] bg-card rounded-sm shadow-lg overflow-hidden flex flex-col">
        <header className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-[15px] font-semibold tracking-tightish">
              {targetUserId
                ? `Editar fichaje${targetName ? ` de ${targetName}` : ''}`
                : 'Solicitar corrección'}
            </h2>
            <p className="font-mono text-[11px] text-muted-foreground mt-0.5">
              Fecha: {targetDate}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Cerrar"
            className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-line-strong bg-card text-foreground hover:border-foreground transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4">
          {items.length === 0 ? (
            <p className="text-[13px] text-muted-foreground">
              Añade los cambios que quieres proponer. Cada solicitud puede combinar varios items.
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {items.map((it) => (
                <ItemRow
                  key={it.key}
                  item={it}
                  punches={punches}
                  onChange={(patch) => updateItem(it.key, patch)}
                  onRemove={() => removeItem(it.key)}
                />
              ))}
            </ul>
          )}

          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => addItem('ADD')}>
              <Plus className="h-3.5 w-3.5" />
              Añadir fichaje
            </Button>
            {availablePunches.length > 0 ? (
              <>
                <PunchPicker
                  label="Modificar fichaje"
                  punches={availablePunches}
                  onPick={(p) => addItem('MODIFY', p.id)}
                />
                <PunchPicker
                  label="Anular fichaje"
                  punches={availablePunches}
                  onPick={(p) => addItem('VOID', p.id)}
                />
              </>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="corr-reason">
              Motivo <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="corr-reason"
              rows={3}
              minLength={5}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explica brevemente por qué pides la corrección…"
            />
            <p
              className={cn(
                'text-[11px]',
                reason.trim().length > 0 && reason.trim().length < 5
                  ? 'text-destructive'
                  : 'text-muted-foreground',
              )}
            >
              Mínimo 5 caracteres ({reason.trim().length}/500).
            </p>
          </div>

          {items.length === 0 ? (
            <p className="text-[11px] text-muted-foreground">
              Añade al menos un cambio (entrada, salida, modificación o anulación).
            </p>
          ) : null}

          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </div>

        <footer className="flex justify-end gap-2 px-6 py-3.5 border-t border-border bg-paper">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            disabled={createMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => createMutation.mutate()}
            disabled={!canSubmit}
          >
            {createMutation.isPending ? 'Enviando…' : 'Enviar solicitud'}
          </Button>
        </footer>
      </div>
    </div>
  );
}

function buildPayloadItem(it: DraftItem, targetDate: string) {
  const proposedEventAt =
    it.proposedTime && it.action !== 'VOID'
      ? new Date(`${targetDate}T${it.proposedTime}:00`).toISOString()
      : undefined;
  return {
    action: it.action,
    targetPunchId: it.targetPunchId,
    proposedEventAt,
    proposedType: it.action === 'VOID' ? undefined : it.proposedType,
    proposedDeclaredLocation: it.action === 'VOID' ? undefined : it.declaredLocation,
  };
}

const ACTION_TONE: Record<CorrectionAction, 'green' | 'amber' | 'red'> = {
  ADD: 'green',
  MODIFY: 'amber',
  VOID: 'red',
};

const ACTION_LABEL_LONG: Record<CorrectionAction, string> = {
  ADD: 'Añadir fichaje',
  MODIFY: 'Modificar',
  VOID: 'Anular',
};

function ItemRow({
  item,
  punches,
  onChange,
  onRemove,
}: {
  item: DraftItem;
  punches: DayPunch[];
  onChange: (patch: Partial<DraftItem>) => void;
  onRemove: () => void;
}) {
  const orig = punches.find((p) => p.id === item.targetPunchId);

  const selectCls =
    'h-9 w-full rounded-sm border border-input bg-card px-3 text-base text-foreground focus-visible:border-icaria-600 focus-visible:outline-none transition-colors';

  return (
    <li className="rounded-sm border border-border bg-card p-4 flex flex-col gap-2.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <Badge tone={ACTION_TONE[item.action]}>{ACTION_LABEL_LONG[item.action]}</Badge>
          {orig ? (
            <span className="font-mono text-[12px] text-muted-foreground tabular-nums">
              {orig.type} · {formatTime(orig.eventAt)}
            </span>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="h-7 w-7 inline-flex items-center justify-center rounded-sm hover:bg-paper text-muted-foreground hover:text-destructive transition-colors"
          aria-label="Quitar item"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {item.action !== 'VOID' ? (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label className="text-[11px]">Tipo</Label>
              <select
                value={item.proposedType ?? orig?.type ?? 'IN'}
                onChange={(e) => onChange({ proposedType: e.target.value as CorrectionPunchType })}
                className={selectCls}
              >
                <option value="IN">Entrada</option>
                <option value="OUT">Salida</option>
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-[11px]">Hora propuesta</Label>
              <Input
                type="time"
                value={item.proposedTime ?? (orig ? localTime(orig.eventAt) : '')}
                onChange={(e) => onChange({ proposedTime: e.target.value })}
                className={cn(!item.proposedTime && item.action === 'ADD' && 'border-warning/60')}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-[11px]">Ubicación</Label>
            <div className="inline-flex w-fit rounded-sm border border-input overflow-hidden">
              <button
                type="button"
                onClick={() => onChange({ declaredLocation: 'OFFICE' })}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3.5 h-9 text-[13px] transition-colors',
                  item.declaredLocation === 'OFFICE'
                    ? 'bg-foreground text-card'
                    : 'bg-card text-muted-foreground hover:text-foreground',
                )}
              >
                <Building2 className="h-3.5 w-3.5" />
                Oficina
              </button>
              <button
                type="button"
                onClick={() => onChange({ declaredLocation: 'REMOTE' })}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3.5 h-9 text-[13px] border-l border-input transition-colors',
                  item.declaredLocation === 'REMOTE'
                    ? 'bg-foreground text-card'
                    : 'bg-card text-muted-foreground hover:text-foreground',
                )}
              >
                <HomeIcon className="h-3.5 w-3.5" />
                Remoto
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </li>
  );
}

function PunchPicker({
  label,
  punches,
  onPick,
}: {
  label: string;
  punches: DayPunch[];
  onPick: (p: DayPunch) => void;
}) {
  return (
    <details className="relative">
      <summary className="cursor-pointer list-none">
        <span className="inline-flex items-center gap-2 px-3 h-8 rounded-sm border border-line-strong bg-transparent text-[13px] font-medium text-foreground hover:bg-card hover:border-foreground transition-colors">
          {label}
        </span>
      </summary>
      <div className="absolute z-10 mt-1 bg-card border border-border rounded-sm shadow-lg min-w-[180px] py-1">
        {punches.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={(e) => {
              (e.currentTarget.closest('details') as HTMLDetailsElement | null)?.removeAttribute(
                'open',
              );
              onPick(p);
            }}
            className="w-full text-left px-3 py-1.5 text-[13px] hover:bg-paper transition-colors flex items-center gap-2"
          >
            <span className="font-mono inline-block w-10 font-semibold">{p.type}</span>
            <span className="font-mono tabular-nums">{formatTime(p.eventAt)}</span>
          </button>
        ))}
      </div>
    </details>
  );
}

function localTime(iso: string): string {
  const d = new Date(iso);
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}
